/**
 * HOW MANY OF ONE ROW A SHOPPER MAY PUT IN ONE ORDER — the shop's answer, in
 * the one place every surface reads it.
 *
 * The product page has capped its quantity stepper at the shelf since it was
 * written. The CART never asked. Forty clicks of the cart drawer's `+` on a
 * watch with one unit produced a line of 41 at ₪8,159, which reached
 * `/dk-store/checkout`, survived an SMS login and a full delivery form, and
 * was then refused by the server — in English, on a Hebrew shop. So the rule
 * moved out of the product page and into here, and the cart, the drawer and
 * the checkout all read the same function.
 *
 * ── WHAT AN UNTRACKED PRODUCT MEANS, AND WHY ────────────────────────────────
 *
 * `trackInventory` is a per-PRODUCT switch and 11 of dk-store's 530 products
 * have it off. It is tempting to read their `stockOnHand` — every one of the
 * 13 rows under them says 0 — and call them sold out. That would be wrong in
 * the most expensive direction: those zeros are the ABSENCE of a figure, not a
 * figure. The importer writes `stockOnHand: null` for an untracked row and the
 * column defaults to 0 (`woo-catalog.map.ts`).
 *
 * What the merchant's own data says about those 11, read from the import's
 * lossiness report rather than assumed: NOT ONE of dk-store's 530 source rows
 * was silent about quantity (`STOCK_NOT_STATED` = 0). Nine of the eleven
 * stated `add_to_cart.maximum = 9999`, which is WooCommerce's own spelling of
 * "any quantity" — the merchant is saying he can fill any order. The other two
 * are `STOCK_CAP_DROPPED`: their rows disagreed with each other, some naming a
 * ceiling and some not, and the importer refused to invent one for the silent
 * rows because "a number we made up is a number the order path will enforce
 * against a shopper".
 *
 * So: AN UNTRACKED PRODUCT HAS NO CEILING, and this function says `null` —
 * not `Infinity`, and not 0. Three consequences, all deliberate:
 *
 *   · the cart's `+` never disables on one, because the shop never said stop;
 *   · no "only N left" is ever shown for one, because there is no N;
 *   · the SERVER will not refuse one either (`stock-movement.core.ts` filters
 *     on `trackInventory: true`), so the interface and the server agree —
 *     which is the actual requirement. A client-side ceiling the server does
 *     not share would refuse sales the merchant can fill, and would be a
 *     number this file invented.
 *
 * The two `STOCK_CAP_DROPPED` products are a data defect, and the honest place
 * to fix them is the import — they are already named in the run's lossiness
 * report. Papering over them here would hide them there.
 */

/** The shop stated no ceiling for this row. Distinct from a ceiling of 0. */
export const UNCOUNTED = null;

/**
 * The most of ONE variant a shopper may order, or `UNCOUNTED`.
 *
 * `product` is the card/detail payload (it carries `trackInventory`);
 * `variant` is the row being bought (it carries `stockOnHand`). Both are
 * needed because the switch and the figure live at different levels — reading
 * `stockOnHand` without checking the switch is exactly the bug described
 * above.
 */
export function variantPurchaseCap(product, variant) {
  if (!product || product.trackInventory !== true) return UNCOUNTED;
  const n = Number(variant?.stockOnHand);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.trunc(n);
}

/** The whole product's ceiling — the sum of the rows the shopper can pick
 *  from. Used by a card that has no chosen variant yet. */
export function productPurchaseCap(product) {
  if (!product || product.trackInventory !== true) return UNCOUNTED;
  const rows = Array.isArray(product.variants) ? product.variants : [];
  let total = 0;
  for (const v of rows) {
    const n = Number(v?.stockOnHand);
    if (Number.isFinite(n) && n > 0) total += Math.trunc(n);
  }
  return total;
}

/** True when this row has a ceiling the shop actually stated. */
export function hasCap(cap) {
  return typeof cap === 'number' && Number.isFinite(cap);
}

/**
 * Bound a wanted quantity by a cap.
 *
 * Never returns less than 1 for a line that exists: a cart line at 0 is a line
 * the shopper did not remove, and silently deleting it is worse than showing
 * one they cannot buy. A capped-to-0 row is refused at the door instead
 * (`overCap` below), where it can be explained.
 */
export function clampQuantity(qty, cap) {
  const wanted = Math.trunc(Number(qty) || 0);
  const floor = Math.max(1, wanted);
  if (!hasCap(cap)) return floor;
  return Math.max(1, Math.min(floor, cap));
}

/** True when this line asks for more than the shop said it has. */
export function overCap(qty, cap) {
  if (!hasCap(cap)) return false;
  return Math.trunc(Number(qty) || 0) > cap;
}

/**
 * The cap a stored cart line carries.
 *
 * Written into the snapshot by every add-to-cart door, so the drawer can
 * refuse a 41st click with no network round trip. Lines stored before this
 * existed carry nothing, and read as `UNCOUNTED` — which is why the drawer and
 * the checkout also re-read the live shelf in ONE batched request rather than
 * trusting the snapshot alone.
 */
export function lineCap(item) {
  const raw = item?.snapshot?.maxQty;
  if (raw === undefined || raw === null) return UNCOUNTED;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return UNCOUNTED;
  return Math.trunc(n);
}

/**
 * The tighter of two ceilings, where NEITHER is more authoritative.
 *
 * Used when two doors meet on the same line: the line already in the cart and
 * the add being merged into it. A door that states no ceiling is a door that
 * does not KNOW one — a card rendered from a payload that predates this — and
 * must never widen a ceiling another door already established. Only a lower
 * number wins.
 */
export function tighten(a, b) {
  if (!hasCap(a)) return hasCap(b) ? b : UNCOUNTED;
  if (!hasCap(b)) return a;
  return Math.min(a, b);
}

/**
 * The tighter of what the line remembers and what the shelf now says.
 *
 * A live reading always wins when it is lower — stock only being taken between
 * the add and the checkout is the common case. When the live read says
 * UNCOUNTED (the merchant switched tracking off since) the line's remembered
 * ceiling is dropped too, because a stale number is not a ceiling the server
 * will enforce.
 */
export function tightestCap(remembered, live) {
  if (live === undefined) return remembered;
  if (!hasCap(live)) return UNCOUNTED;
  if (!hasCap(remembered)) return live;
  return Math.min(remembered, live);
}

/**
 * Which lines of a cart the shop cannot fill, given the live caps.
 *
 * Returns one entry per offending line so the checkout can name them — a
 * refusal that says "something is out of stock" on a nine-line basket is not a
 * refusal a shopper can act on.
 */
export function linesOverCap(items, capByVariantId) {
  const out = [];
  for (const it of Array.isArray(items) ? items : []) {
    if (!it?.variantId) continue;
    const cap = tightestCap(lineCap(it), capByVariantId?.get?.(it.variantId));
    if (overCap(it.quantity, cap)) {
      out.push({ variantId: it.variantId, quantity: it.quantity, cap, name: it.snapshot?.name ?? null });
    }
  }
  return out;
}
